import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Wrench, Plus, CheckCircle, Clock, Send, AlertCircle } from 'lucide-react'
import { serviceAuftraege as serviceApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { LoadingSpinner, ErrorMessage } from '../../components/ui/EmptyState'
import { formatDatum } from '../../lib/utils'
import { cn } from '../../lib/utils'
import type { ServiceAuftrag } from '../../types'

const STATUS_CONFIG: Record<ServiceAuftrag['status'], { label: string; color: string; icon: React.ReactNode }> = {
  offen: { label: 'Offen', color: 'bg-gray-100 text-gray-700', icon: <AlertCircle size={12} /> },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-700', icon: <Clock size={12} /> },
  abgeschlossen: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-700', icon: <CheckCircle size={12} /> },
  versendet: { label: 'Versendet', color: 'bg-purple-100 text-purple-700', icon: <Send size={12} /> },
}

const FILTER_TABS = [
  { key: '', label: 'Alle' },
  { key: 'offen', label: 'Offen' },
  { key: 'in_bearbeitung', label: 'In Bearb.' },
  { key: 'abgeschlossen', label: 'Abgeschl.' },
]

export function ServiceListePage() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['service', filter],
    queryFn: () => serviceApi.list(filter ? { status: filter } : undefined),
  })

  return (
    <>
      <TopBar
        title="Service"
        rightAction={
          <Button size="sm" icon={<Plus size={14} />} onClick={() => navigate('/service/neu')}>
            Neu
          </Button>
        }
      />

      {/* Filter Tabs */}
      <div className="px-4 pt-3">
        <div className="flex gap-1.5 bg-gray-100 rounded-xl p-1">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={cn(
                'flex-1 text-xs font-medium py-1.5 rounded-lg transition-all',
                filter === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-3 space-y-3 pb-8">
        {isLoading && <LoadingSpinner />}
        {error && <ErrorMessage message="Fehler beim Laden" />}
        {!isLoading && !error && (data ?? []).length === 0 && (
          <Card>
            <div className="py-8 text-center">
              <Wrench size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Keine Service-Aufträge</p>
              <Button className="mt-4" size="sm" onClick={() => navigate('/service/neu')}>
                Ersten Auftrag erstellen
              </Button>
            </div>
          </Card>
        )}

        {(data ?? []).map((auftrag) => {
          const sc = STATUS_CONFIG[auftrag.status]
          return (
            <Card key={auftrag.id} clickable onClick={() => navigate(`/service/${auftrag.id}`)}>
              <div className="flex items-start gap-3">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5',
                  auftrag.status === 'offen' ? 'bg-gray-100' :
                  auftrag.status === 'in_bearbeitung' ? 'bg-blue-50' :
                  auftrag.status === 'abgeschlossen' ? 'bg-green-50' : 'bg-purple-50'
                )}>
                  <Wrench size={18} className={
                    auftrag.status === 'offen' ? 'text-gray-500' :
                    auftrag.status === 'in_bearbeitung' ? 'text-blue-600' :
                    auftrag.status === 'abgeschlossen' ? 'text-green-600' : 'text-purple-600'
                  } />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-xs text-gray-400 font-mono">{auftrag.nummer}</p>
                    <span className={cn('flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full', sc.color)}>
                      {sc.icon}{sc.label}
                    </span>
                    {auftrag.garantie ? (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Garantie</span>
                    ) : (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">Bezahlt</span>
                    )}
                  </div>
                  <p className="font-semibold text-gray-900 truncate">{auftrag.kunde_firma ?? auftrag.kunde_name}</p>
                  <p className="text-sm text-gray-600 truncate">{auftrag.geraet_modell}{auftrag.seriennummer ? ` · ${auftrag.seriennummer}` : ''}</p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-1">{auftrag.beschreibung}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-gray-400">{formatDatum(auftrag.erstellt_am)}</p>
                  {auftrag.kosten > 0 && (
                    <p className="text-sm font-semibold text-gray-900 mt-1">CHF {auftrag.kosten.toFixed(2)}</p>
                  )}
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </>
  )
}
